/**
 * Custom Hooks.
 */
import React from 'react';
import ResizeObserverPolyfill from 'resize-observer-polyfill';

/**
 * Executes an effect only after `deps` have updated, not
 * on initial mount of the component using it.
 *
 * @param fn Effect to run.
 * @param deps Dependencies to watch.
 */
export const useDidUpdateEffect = (fn: () => void, deps: any[]): void => {
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (mounted.current) {
      fn();
    } else {
      mounted.current = true;
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, deps);
};

/**
 * Observers an element and returns it's dimensions when the element is resized
 * and returns the ref to set resize observer on
 */
export const useDimensions = <T>(): [
  { ref: React.RefObject<T & HTMLElement> },
  { left: number; top: number; width: number; height: number }
] => {
  const [dimensions, setDimensions] = React.useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0
  });
  const ref = React.useRef<T & HTMLElement>(null);
  const [observer] = React.useState(
    () =>
      new ResizeObserverPolyfill(([entry]: ResizeObserverEntry[]) =>
        setDimensions(entry.contentRect)
      )
  );

  React.useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [observer]);
  return [{ ref }, dimensions];
};

/**
 * Returns value from ref from last update (previous props)
 * @param value Initial value
 */
export const useLast = (value: boolean): boolean => {
  const ref = React.useRef<boolean>(value);
  const { current } = ref;
  React.useEffect(
    () =>
      (() => {
        ref.current = value;
      })(),
    [value, current]
  );
  return ref.current;
};

/**
 * Attaches a `window` level event listener for the user hitting the
 * `esc` key.
 *
 * @param handler Callback to invoke when `esc` is hit.
 */
export const useESCKey = (handler: () => void): void => {
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.keyCode === 27 || e.key === 'Escape') {
        handler();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [handler]);
};

/**
 * Sends error to console (only in development environments)
 * @param message - string to display in console
 * @param validation - function (that returns boolean) or boolean to verify
 */
export const useDevWarning = (
  message: string,
  validation: (() => boolean) | boolean
): void => {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (typeof validation === 'function') {
        if (validation()) {
          /* eslint-disable-next-line no-console */
          console.warn(message);
        }
      } else if (validation) {
        /* eslint-disable-next-line no-console */
        console.warn(message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Creates event listeners to track whether the user is using the keyboard or mouse
 * @param toggler - setter function from your React.useState hook
 */
export const useKeyboardMode = (
  toggler: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  React.useEffect(() => {
    let handleMouse: () => void;
    const handleTab = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') {
        toggler(true);
        window.removeEventListener('keydown', handleTab);
        window.addEventListener('mousedown', handleMouse);
      }
    };
    handleMouse = (): void => {
      toggler(false);
      window.removeEventListener('mousedown', handleMouse);
      window.addEventListener('keydown', handleTab);
    };
    window.addEventListener('keydown', handleTab);

    return () => {
      window.removeEventListener('keydown', handleTab);
      window.removeEventListener('mousedown', handleMouse);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
};
